import {ActionTypes} from './actions';
import {TEmailContextType, TEmailReducerType, TEmailStateType, TEmailTemplateState} from './types';
import {renderDefault} from '../utils/common';
import {replaceTitlesWithIds} from '../utils/treeActions';
import {TAlertMessageDictionary} from '../../Automations/types';

const updateContext = (context: TEmailContextType) => {
    return (
        stateName?: keyof TEmailStateType,
        payloadValue?: (typeof context.states)[keyof TEmailStateType]
    ) => ({...context, states: {...context.states, [stateName]: payloadValue}});
};

const resetContext = (context: TEmailContextType, data: TAlertMessageDictionary) => () => ({
    ...context.handlers,
    states: {
        ...renderDefault(data).states,
        emailEditor: context.states.emailEditor
    }
});

const updateTemplate = (
    context: TEmailContextType,
    payload: Partial<TEmailTemplateState>,
    isDirty?: boolean
) => {
    const language = context.states.language;
    const templates = context.states.templates;
    const currentTemplate = templates[language] || {};

    return {
        ...context,
        states: {
            ...context.states,
            templates: {
                ...templates,
                [language]: {
                    ...currentTemplate,
                    ...payload
                }
            },
            ...(isDirty !== undefined ? {isDirty} : {})
        }
    };
};

export const emailReducer: TEmailReducerType = (context, action, messagesValues) => {
    const update = updateContext(context);
    const reset = resetContext(context, messagesValues);

    const states = context.states;

    switch (action.type) {
        case ActionTypes.OPEN_EMAILEDITOR:
            return {...context};

        case ActionTypes.CLOSE_EMAILEDITOR:
            return reset();

        case ActionTypes.SAVE_EMAILEDITOR:
            return null;

        case ActionTypes.PERSONALIZE_UPDATE:
            return update('personalizeState', action.payload?.personalizeState);

        case ActionTypes.HYPERLINK_STATES_UPDATE:
            return update('hyperlinkState', {
                ...context.states.hyperlinkState,
                ...action.payload?.hyperlinkStates
            });

        case ActionTypes.HYPERLINK_ACTIVE_UPDATE:
            return update('hyperlinkState', {
                ...context.states.hyperlinkState,
                active: action.payload?.isActive
            });

        case ActionTypes.IMAGE_UPDATE:
            return update('imageState', action.payload?.imageState);

        case ActionTypes.LANGUAGE_UPDATE:
            return update('language', action.payload?.language);

        case ActionTypes.UPDATE_EMAIL_EDITOR:
            return update('emailEditor', action.payload?.editor);

        case ActionTypes.UPDATE_EMAIL_HTML:
            const emailHtml = action.payload?.emailHtml;

            return updateTemplate(context, {emailHtml});

        case ActionTypes.UPDATE_EMAIL_SUBJECT:
            const subject = action.payload?.subject;
            return updateTemplate(context, {editorSubject: subject}, true);

        case ActionTypes.UPDATE_EMAIL_SUBJECT_IDS:
            const editorSubjectIds = replaceTitlesWithIds(
                action.payload?.subject,
                action.payload?.treeValues
            );

            return updateTemplate(context, {editorSubjectIds});

        case ActionTypes.UPDATE_DIRTY_STATE:
            const isDirty = action.payload?.isDirty;
            return {
                ...context,
                states: {
                    ...states,
                    isDirty
                }
            };
        case ActionTypes.UPDATE_AI_LOADING_STATE:
            const aiLoading = action.payload?.aiLoading;
            return {
                ...context,
                states: {
                    ...states,
                    aiLoading
                }
            };

        default:
            return context;
    }
};
